<template>
  <div>
    <page-title-bar :customTitle="customTitle"></page-title-bar>
    <v-container fluid grid-list-xl pt-0>
      <v-layout row wrap justify-center>
        <v-flex xs12>
          <v-toolbar :elevation="0" height="auto">
            <v-layout row justify-space-between align-center style="padding: 32px">
              <!-- Month Picker -->
              <v-flex xs12 md3>
                <div style="max-width: 290px">
                  <v-menu :close-on-content-click="false" v-model="menu_month_picker" :nudge-right="40" lazy
                    transition="scale-transition" offset-y full-width min-width="290px" max-width="290px"
                    :disabled="loader">
                    <v-text-field slot="activator" v-model="chosen_month" label="Chọn tháng" :disabled="loader"
                      prepend-icon="event" readonly hide-details></v-text-field>
                    <v-date-picker min="2025-03" :max="maxMonth" locale="vi-VI" type="month" v-model="chosen_month"
                      @input="menu_month_picker = false"></v-date-picker>
                  </v-menu>
                </div>
              </v-flex>

              <!-- Status Info -->
              <v-flex xs12 md6>
                <v-layout row wrap align-center justify-center>
                  <v-flex shrink>
                    <div class="text-center px-2">
                      <div class="caption grey--text text--darken-1 mb-1">Trạng thái tháng được chọn</div>
                      <v-chip 
                        v-if="is_locked" 
                        color="green" 
                        text-color="white" 
                        small 
                        label
                        class="font-weight-medium"
                      >
                        <v-icon left x-small>check_circle</v-icon>
                        Đã chốt
                      </v-chip>
                      <v-chip 
                        v-else 
                        color="orange" 
                        text-color="white" 
                        small 
                        label
                        class="font-weight-medium"
                      >
                        <v-icon left x-small>schedule</v-icon>
                        Chưa chốt
                      </v-chip>
                    </div>
                  </v-flex>
                  <v-flex shrink>
                    <div class="text-center px-2">
                      <div class="caption grey--text text--darken-1 mb-1">Tháng gần nhất đã chốt</div>
                      <div v-if="latestLockedMonth" class="body-2 font-weight-medium primary--text">
                        {{ formatMonthDisplay(latestLockedMonth) }}
                      </div>
                      <div v-else class="body-2 grey--text">
                        Chưa có
                      </div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-flex>

              <!-- Action Button -->
              <v-flex xs12 md3 class="text-right">
                <v-btn color="success" class="mr-2" @click="exportExcel" :loading="exporting" :disabled="loader || !report_data">
                  <v-icon left small>cloud_download</v-icon>
                  Xuất Excel
                </v-btn>

                <v-btn v-if="!is_locked" color="primary" :loading="locking" :disabled="loader || !canLock"
                  @click="lockReport">
                  <v-icon left small>lock</v-icon>
                  CHỐT BÁO CÁO
                </v-btn>
                <v-btn v-else color="grey" disabled flat>
                  <v-icon left small>lock</v-icon>
                  ĐÃ CHỐT
                </v-btn>
              </v-flex>
            </v-layout>
          </v-toolbar>

          <!-- Warning message if can't lock -->
          <v-layout row wrap v-if="!canLock && !is_locked && !loader">
            <v-flex xs12>
              <v-alert :value="true" type="info" outline>
                <strong>Lưu ý:</strong> Cần chốt báo cáo tháng {{ formatMonthDisplay(previousMonth) }} trước khi có thể chốt tháng {{ formatMonthDisplay(chosen_month) }}.
              </v-alert>
            </v-flex>
          </v-layout>

          <div class="mt-3">
            <v-card>
              <v-card-title class="headline">
                Báo cáo tình hình tài chính tháng {{ formatMonthDisplay(chosen_month) }}
              </v-card-title>
              <v-divider></v-divider>

              <!-- Loading State -->
              <v-card-text v-if="loader" class="text-center pa-5">
                <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                <p class="mt-3">Đang tải báo cáo...</p>
              </v-card-text>

              <!-- Report Content -->
              <v-card-text v-else-if="report_data">
                <div class="table-responsive">
                  <table class="monthly-report-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="min-width: 350px">Chỉ tiêu</th>
                        <th class="text-right" style="min-width: 180px">Số dư</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- TÀI SẢN -->
                      <tr class="section-header">
                        <td colspan="2"><strong>TÀI SẢN</strong></td>
                      </tr>
                      
                      <!-- Tiền mặt -->
                      <tr>
                        <td>Tiền mặt</td>
                        <td class="text-right">{{ formatFinancialValue(report_data.tien_mat) }}</td>
                      </tr>
                      
                      <!-- Ngân hàng - Expandable -->
                      <tr class="expandable-row" @click="toggleBankDetail">
                        <td>
                          Ngân hàng
                          <v-icon small class="ml-1">{{ showBankDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right">{{ formatFinancialValue(report_data.ngan_hang) }}</td>
                      </tr>
                      <template v-if="showBankDetail && report_data.ngan_hang_detail">
                        <tr v-for="(bank, idx) in report_data.ngan_hang_detail" :key="`bank_${idx}`" class="sub-row">
                          <td class="pl-5">- {{ bank.text }}</td>
                          <td class="text-right">{{ formatFinancialValue(bank.amount) }}</td>
                        </tr>
                      </template>
                      
                      <!-- Phải thu khách hàng - Expandable -->
                      <tr class="expandable-row" @click="toggleCustomerDebtDetail">
                        <td>
                          Phải thu khách hàng
                          <v-icon small class="ml-1">{{ showCustomerDebtDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td class="text-right">{{ formatFinancialValue(report_data.phai_thu_khach_hang) }}</td>
                      </tr>
                      <template v-if="showCustomerDebtDetail">
                        <tr v-for="(room, idx) in report_data.phai_thu_khach_hang_detail" :key="`room_${idx}`" class="sub-row">
                          <td class="pl-5">- {{ room.room_name }}</td>
                          <td class="text-right">{{ formatFinancialValue(room.amount) }}</td>
                        </tr>
                        <tr v-if="report_data.cong_no_nho_khac !== 0" class="sub-row">
                          <td class="pl-5">- Các công nợ nhỏ khác</td>
                          <td class="text-right">{{ formatFinancialValue(report_data.cong_no_nho_khac) }}</td>
                        </tr>
                      </template>
                      
                      <!-- Công nợ nhà trọ -->
                      <tr>
                        <td>Công nợ nhà trọ</td>
                        <td class="text-right">{{ formatFinancialValue(report_data.cong_no_nha_tro) }}</td>
                      </tr>
                      
                      <!-- Phải thu anh Long -->
                      <tr>
                        <td>Phải thu anh Long</td>
                        <td class="text-right">{{ formatFinancialValue(report_data.phai_thu_anh_long) }}</td>
                      </tr>
                      
                      <!-- Phải thu khác -->
                      <tr>
                        <td>Phải thu khác</td>
                        <td class="text-right">{{ formatFinancialValue(report_data.phai_thu_khac) }}</td>
                      </tr>
                      
                      <!-- TỔNG CỘNG TÀI SẢN -->
                      <tr class="total-row">
                        <td><strong>TỔNG CỘNG TÀI SẢN</strong></td>
                        <td class="text-right"><strong>{{ formatFinancialValue(report_data.tong_cong_tai_san) }}</strong></td>
                      </tr>
                      
                      <!-- NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU -->
                      <tr class="section-header">
                        <td colspan="2"><strong>NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU</strong></td>
                      </tr>
                      
                      <!-- Nợ dài hạn -->
                      <tr>
                        <td>Nợ dài hạn</td>
                        <td class="text-right">{{ formatFinancialValue(report_data.no_dai_han) }}</td>
                      </tr>
                      
                      <!-- Nợ phải trả (sub-item) -->
                      <tr class="sub-row">
                        <td class="pl-5">- Nợ phải trả</td>
                        <td class="text-right">{{ formatFinancialValue(report_data.no_phai_tra) }}</td>
                      </tr>
                      
                      <!-- Vay (sub-item) -->
                      <tr class="sub-row">
                        <td class="pl-5">- Vay</td>
                        <td class="text-right">{{ formatFinancialValue(report_data.vay) }}</td>
                      </tr>
                      
                      <!-- LNCPP -->
                      <tr>
                        <td>LNCPP (Lợi nhuận chưa phân phối)</td>
                        <td class="text-right">{{ formatFinancialValue(report_data.lncpp) }}</td>
                      </tr>
                      
                      <!-- TỔNG CỘNG NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU -->
                      <tr class="total-row">
                        <td><strong>TỔNG CỘNG NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU</strong></td>
                        <td class="text-right"><strong>{{ formatFinancialValue(report_data.tong_cong_no_phai_tra_va_von_chu_so_huu) }}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-card-text>

              <!-- No Data State -->
              <v-card-text v-else class="text-center pa-5">
                <p class="grey--text">Chưa có dữ liệu báo cáo</p>
              </v-card-text>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import api from "../../api";
import { ExcelExporter } from "../../helpers/ExcelExporter";
import swal from "sweetalert2";

export default {
  name: "BaoCaoTinhHinhTaiChinhTheoThang",
  data() {
    return {
      loader: false,
      locking: false,
      exporting: false,
      report_data: null,
      is_locked: false,
      locked_at: null,
      locked_by: null,
      canLock: true,
      menu_month_picker: false,
      chosen_month: moment().subtract(1, "month").format("YYYY-MM"),
      locked_months: [],
      // Expand/collapse state - mặc định là mở
      showBankDetail: true,
      showCustomerDebtDetail: true,
    };
  },
  watch: {
    chosen_month: function () {
      this.getData();
    },
  },
  computed: {
    customTitle() {
      return "Báo cáo tình hình tài chính theo tháng";
    },
    maxMonth() {
      return moment().format("YYYY-MM");
    },
    previousMonth() {
      return moment(this.chosen_month).subtract(1, "month").format("YYYY-MM");
    },
    latestLockedMonth() {
      if (!this.locked_months || this.locked_months.length === 0) return null;
      // Sort locked months descending and get the latest
      const sorted = [...this.locked_months].sort((a, b) => {
        return moment(b.month).diff(moment(a.month));
      });
      return sorted.length > 0 && sorted[0].month ? sorted[0].month : null;
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    formatMonthDisplay(month) {
      return moment(month).format("MM/YYYY");
    },
    toggleBankDetail() {
      this.showBankDetail = !this.showBankDetail;
    },
    toggleCustomerDebtDetail() {
      this.showCustomerDebtDetail = !this.showCustomerDebtDetail;
    },
    async getData() {
      this.loader = true;
      this.report_data = null;

      try {
        const result = await api.getMonthlyFinancialReport(`month=${this.chosen_month}`);

        if (result.status === 200) {
          this.report_data = result.data.report_data;
          this.is_locked = result.data.is_locked;
          this.locked_at = result.data.locked_at;
          this.locked_by = result.data.locked_by;
        } else {
          swal.fire("Lỗi", result.message || "Không thể tải báo cáo", "error");
        }
      } catch (error) {
        console.error("Error loading report:", error);
        swal.fire("Lỗi", "Không thể tải báo cáo", "error");
      }

      this.loader = false;
      this.checkCanLock();
    },
    async checkCanLock() {
      // March 2025 is always lockable if not locked
      if (this.chosen_month === "2025-03") {
        this.canLock = true;
        return;
      }

      try {
        const result = await api.getLockedMonths();
        if (result.status === 200) {
          this.locked_months = result.data.locked_months || [];
          const lockedMonths = this.locked_months.map(m => m.month);
          this.canLock = lockedMonths.includes(this.previousMonth);
        }
      } catch (error) {
        console.error("Error checking locked months:", error);
        this.canLock = false;
      }
    },
    async lockReport() {
      const confirm = await swal.fire({
        title: "Xác nhận chốt báo cáo",
        text: `Bạn có chắc chắn muốn chốt báo cáo tháng ${this.formatMonthDisplay(this.chosen_month)}? Sau khi chốt sẽ không thể thay đổi.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Chốt báo cáo",
        cancelButtonText: "Hủy",
      });

      if (!confirm.value) return;

      this.locking = true;

      try {
        const result = await api.lockFinancialReport({ month: this.chosen_month });

        if (result.status === 200) {
          swal.fire("Thành công", result.data.message, "success");
          this.getData();
        } else {
          swal.fire("Lỗi", result.message || "Không thể chốt báo cáo", "error");
        }
      } catch (error) {
        console.error("Error locking report:", error);
        swal.fire("Lỗi", "Không thể chốt báo cáo", "error");
      }

      this.locking = false;
    },
    formatFinancialValue(val) {
      if (val === null || val === undefined) return '-';
      const num = Number(val);
      if (num === 0) return '0';
      if (num < 0) return `(${Math.abs(num).toLocaleString('vi-VN')})`;
      return num.toLocaleString('vi-VN');
    },
    async exportExcel() {
        if (!this.report_data) return;
        this.exporting = true;
        try {
            const data = [];
            
            // --- ASSETS ---
            data.push({ name: 'TÀI SẢN', style: { bold: true, background: 'FFE3F2FD' } });
            
            // Cash
            data.push({ name: 'Tiền mặt', amount: this.report_data.tien_mat, outlineLevel: 0 });
            
            // Bank
            data.push({ name: 'Ngân hàng', amount: this.report_data.ngan_hang, outlineLevel: 0 });
            if (this.report_data.ngan_hang_detail) {
                this.report_data.ngan_hang_detail.forEach(b => {
                    data.push({ name: b.text, amount: b.amount, style: { indent: 1 }, outlineLevel: 1 });
                });
            }

            // Customer Debt
            data.push({ name: 'Phải thu khách hàng', amount: this.report_data.phai_thu_khach_hang, outlineLevel: 0 });
            if (this.report_data.phai_thu_khach_hang_detail) {
                this.report_data.phai_thu_khach_hang_detail.forEach(r => {
                    data.push({ name: r.room_name, amount: r.amount, style: { indent: 1 }, outlineLevel: 1 });
                });
            }
            if (this.report_data.cong_no_nho_khac !== 0) {
                 data.push({ name: 'Các công nợ nhỏ khác', amount: this.report_data.cong_no_nho_khac, style: { indent: 1 }, outlineLevel: 1 });
            }

            // Other Assets
            data.push({ name: 'Công nợ nhà trọ', amount: this.report_data.cong_no_nha_tro });
            data.push({ name: 'Phải thu anh Long', amount: this.report_data.phai_thu_anh_long });
            data.push({ name: 'Phải thu khác', amount: this.report_data.phai_thu_khac });

            // Total Assets
            data.push({ name: 'TỔNG CỘNG TÀI SẢN', amount: this.report_data.tong_cong_tai_san, style: { bold: true, background: 'FFF3E0' } });


            // --- LIABILITIES & EQUITY ---
            data.push({ name: 'NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU', style: { bold: true, background: 'FFE3F2FD' } });
            
            // Long term debt
            data.push({ name: 'Nợ dài hạn', amount: this.report_data.no_dai_han });
            data.push({ name: 'Nợ phải trả', amount: this.report_data.no_phai_tra, style: { indent: 1 }, outlineLevel: 1 });
            data.push({ name: 'Vay', amount: this.report_data.vay, style: { indent: 1 }, outlineLevel: 1 });

            // LNCPP
            data.push({ name: 'LNCPP (Lợi nhuận chưa phân phối)', amount: this.report_data.lncpp });

            // Total
            data.push({ name: 'TỔNG CỘNG NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU', amount: this.report_data.tong_cong_no_phai_tra_va_von_chu_so_huu, style: { bold: true, background: 'FFF3E0' } });

            await ExcelExporter.exportReport({
                title: 'BÁO CÁO TÌNH HÌNH TÀI CHÍNH',
                subtitle: `Tháng ${this.formatMonthDisplay(this.chosen_month)}`,
                columns: [
                    { header: 'Chỉ tiêu', key: 'name', width: 50 },
                    { header: 'Số dư', key: 'amount', width: 25, align: 'right', format: 'currency' }
                ],
                data: data,
                fileName: `BaoCaoTaiChinh_${this.chosen_month}`
            });

        } catch (error) {
            console.error(error);
            swal.fire("Lỗi", "Không thể xuất file Excel", "error");
        }
        this.exporting = false;
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.monthly-report-table {
  width: 100%;
  border-collapse: collapse;
}

.monthly-report-table th,
.monthly-report-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.monthly-report-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.monthly-report-table .section-header {
  background-color: #e3f2fd;
}

.monthly-report-table .total-row {
  background-color: #fff3e0;
}

.monthly-report-table .sub-row {
  background-color: #fafafa;
}

.monthly-report-table .expandable-row {
  cursor: pointer;
}

.monthly-report-table .expandable-row:hover {
  background-color: #f0f0f0;
}

.monthly-report-table tbody tr:hover {
  background-color: #fafafa;
}

.pl-5 {
  padding-left: 40px !important;
}
</style>
